/**
 * The turn's Distillation tab: every subject each search call judged, and why.
 *
 * The judgements live on LLM_REQUESTS rows, which the turn endpoint does not
 * return — it carries the turn, its actions and its outputs — so this fetches
 * each distillation action's requests itself. Only the `*_distillation` actions
 * are fetched: the planning, permission and gate stages make no LLM calls, so
 * asking for theirs would be a round trip to find nothing.
 *
 * A turn can search more than once, so each call keeps its own table and its own
 * filter rather than being merged into one where "which call was this?" is lost.
 */
import { useState, useEffect, type JSX } from "react";
import adminAPI from "./apiClient";
import type { Action } from "./types";
import { distillationActions, distillationGroups, type DistillationGroup } from "./mcpDistillation";
import DistillationTable from "./DistillationTable";

export interface ActionDistillation {
  action: Action;
  groups: DistillationGroup[];
  /** Null on success; the message to show in place of this action's table otherwise. */
  error: string | null;
}

/**
 * Fetch and parse each distillation action's judgements.
 *
 * One action failing leaves the others rendered — a turn's second search call
 * still answers for itself when the first cannot be loaded. Split out of the
 * component so both shapes are testable without a DOM.
 */
export async function loadTurnDistillation(
  conversationId: string | undefined,
  actions: Action[],
): Promise<ActionDistillation[]> {
  return Promise.all(
    distillationActions(actions).map(async (action) => {
      try {
        const detail = await adminAPI.getAction(conversationId, action.action_id);
        return {
          action,
          groups: distillationGroups(detail.llm_requests, action.action_output),
          error: null,
        };
      } catch (err) {
        return {
          action,
          groups: [],
          error: err instanceof Error ? err.message : "An unknown error occurred",
        };
      }
    }),
  );
}

function ActionPanel({
  entry,
  actionHref,
}: {
  entry: ActionDistillation;
  actionHref: (actionId: string) => string;
}): JSX.Element {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-medium text-gray-700">{entry.action.action_type}</h3>
        <a
          href={actionHref(entry.action.action_id)}
          className="text-xs text-blue-600 hover:underline"
        >
          Open action
        </a>
      </div>
      {entry.error ? (
        <p className="text-sm text-red-600">Could not load this action: {entry.error}</p>
      ) : (
        <DistillationTable groups={entry.groups} />
      )}
    </div>
  );
}

export default function TurnDistillationTab({
  conversationId,
  actions,
  actionHref,
}: {
  conversationId: string | undefined;
  actions: Action[];
  actionHref: (actionId: string) => string;
}): JSX.Element {
  const [entries, setEntries] = useState<ActionDistillation[] | null>(null);

  useEffect(() => {
    let live = true;
    void loadTurnDistillation(conversationId, actions).then((loaded) => {
      if (live) setEntries(loaded);
    });
    // Discarded on unmount and between turns, so a slow fetch cannot land on a
    // turn the admin has already navigated away from.
    return () => {
      live = false;
    };
  }, [conversationId, actions]);

  if (entries === null) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // A distillation that judged nothing — every deal filtered out before the
  // fan-out, say — is silence rather than an empty table, which would read as a
  // failed load.
  const useful = entries.filter((entry) => entry.error || entry.groups.length > 0);
  if (useful.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No distillation recorded for this turn.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {useful.map((entry) => (
        <ActionPanel key={entry.action.action_id} entry={entry} actionHref={actionHref} />
      ))}
    </div>
  );
}
